import axios, {
  AxiosInstance,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { Token } from '../../models';
import { useAthenaLogger, useConfig, useSession } from '../index';
import { makeGuid } from '../../utils';
import { useRef } from 'react';

const skipRefreshUrls = [`/refresh`, `/users/token/`];

const useAxios = (serviceUrl: string, withCredentials: boolean = false) => {
  const { orionToken, session, logout, refreshSessionTimer, transmitToken } =
    useSession();
  const athenaLogger = useAthenaLogger();
  const { investConfig } = useConfig();
  const isLoggingOut = useRef(false);
  const defaultConfig: AxiosRequestConfig = {
    baseURL: investConfig.baseUrl,
    withCredentials,
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const investClient: AxiosInstance = axios.create(defaultConfig);

  investClient.interceptors.request.use(
    async (request) => {
      let newToken: Token = new Token();

      // It would be better to add a param when instatiating a useAxios instance for which
      // token to use instead of passing in a header but Plaid needs to go out
      if (request.headers.useTransmitToken) {
        request.headers.Authorization = `Bearer ${transmitToken.access_token}`;

        delete request.headers.useTransmitToken;
      }

      request.headers = request.headers || {};
      request.headers['correlationid'] = makeGuid();

      return request;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  investClient.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response?.status == 401 || response?.status == 403) {
        logout();
        return Promise.reject(new Error(`Logout due to being unauthorized`));
      }
      const url = response.config.url?.toLowerCase();
      if (!skipRefreshUrls.some((route) => url?.includes(route))) {
        refreshSessionTimer();
      }
      return response;
    },
    async (error) => {
      const status = error?.response?.status;
      const url = error?.config?.url || '';

      if (status === 401) {
        console.error('Unauthorized error detected, logging out', status);
        if (!isLoggingOut.current) {
          isLoggingOut.current = true;
          await logout();
        }
      }
      return Promise.reject(error);
    }
  );

  /**
   * Instance to be used when you are calling invest api and need to attach
   * the IDP Token
   * @param method get, post, put, delete
   * @param url url to attach to the base invest api url
   * @param headersConfig any headers that need to be configured
   * @param includeOrion if the headers should include orion token and external-client-id
   * @returns AxiosInstance to be called
   */
  function investInstance<T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    url: string,
    headersConfig: AxiosRequestConfig = {},
    includeOrion: boolean = false
  ): AxiosPromise<T> {
    investClient.defaults.method = method;
    if (includeOrion) {
      // @ts-ignore
      investClient.defaults.headers['orion-token'] = orionToken.token;
      // @ts-ignore
      investClient.defaults.headers['external-client-id'] =
        session.householdIds.allyHouseholdId ?? '';
    }
    athenaLogger.sendLog(`Request to: ${serviceUrl}${url}`);
    return investClient(`${serviceUrl}${url}`, headersConfig);
  }

  return { investInstance };
};

export default useAxios;
